import React, { useEffect } from 'react';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { usePathname } from 'hooks/usePathname/usePathname';
import { TGetStateResponseData } from 'lendingApi/lapiOverrides.types';
import { useGetStateQuery } from 'lendingApi/lendingApiHooks';
import { useNavigate } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useRouterApplicationId } from '../hooks/useRouterApplicationId';
import { useSignupData } from '../hooks/useSignupData';
import { useSignupDataDE } from '../hooks/useSignupDataDE/useSignupDataDE';
import { STEPS, useSignupStep } from '../hooks/useSignupStep';
import { minimumDataRequirementsSchemaDE } from '../NewSignupFormDE/NewSignUpFormDE.schema';
import { minimumDataRequirementsSchema as minimumDataRequirementsSchemaUK } from '../SignupForm/SignUpForm.schema';

type TProps = {
  children: React.ReactNode;
};
export const StepValidator = ({ children }: TProps) => {
  const pathname = usePathname();
  const navigate = useNavigate();
  const { isDE } = useLocale();
  const applicationId = useRouterApplicationId();
  const { state, stateStatus, stateFetching } = useGetStateQuery();
  const { stateData, applicationData, isLoadingApplication } = useSignupData();
  const { stateData: stateDataDE } = useSignupDataDE();
  const { trackEvent } = useAmplitude();

  const {
    currentStep,
    latestStepAllowed,
    updateLatestAllowedStep,
    refetchState,
  } = useSignupStep();

  const handleMinimumDataRequirementsRedirect = async () => {
    try {
      const schemaToValidate = isDE
        ? minimumDataRequirementsSchemaDE
        : minimumDataRequirementsSchemaUK;

      const schemaStateData = isDE ? stateDataDE : stateData;

      const isValidated = await schemaToValidate.validate({
        ...schemaStateData,
        ...applicationData,
      });

      // Proceed to next step in the flow if the application meets minimum data requirements
      if (isValidated) {
        // Set latest signup step to prevent redirection loop if they hit the back button
        await updateLatestAllowedStep(
          isDE ? 'new/documents' : 'new/productOffers',
        );
        navigate(
          isDE
            ? PATH_NAMES.newApplicationDocuments({ applicationId })
            : PATH_NAMES.newApplicationProductOffers({ applicationId }),
          { replace: true },
        );
        return;
      }
    } catch {
      // Follow normal redirection process if there are no validation errors and someone hits signup directly, then return
      navigate(PATH_NAMES.newApplicationStep1({ applicationId }), {
        replace: true,
      });
      return;
    }
  };

  const handleLatestStepRedirect = async () => {
    const updatedState = (await refetchState()).data
      ?.data as TGetStateResponseData;
    const actualLatestStepAllowed = updatedState.ui?.tip_latest_signup_step;
    const currentStepIndex = STEPS.findIndex((step) => step === currentStep);
    const latestStepAllowedIndex = STEPS.findIndex(
      (step) => step === actualLatestStepAllowed,
    );
    const noAccessToCurrentStep = currentStepIndex > latestStepAllowedIndex;

    if (currentStep === 'new/continue' || noAccessToCurrentStep) {
      trackEvent(AMPLITUDE_EVENTS.latestSignupStepAccessed, {
        'Latest page': latestStepAllowed!,
      });

      navigate(
        PATH_NAMES.newApplication({ step: latestStepAllowed!, applicationId }),
        { replace: true },
      );
    }
  };

  useEffect(() => {
    if (
      !stateFetching &&
      stateStatus === 'success' &&
      !isLoadingApplication &&
      state
    ) {
      // Don't redirect if on page that doesn't require validation and application has not been created in API
      if (currentStep === 'new' && latestStepAllowed) {
        return;
      }
      if (!latestStepAllowed) {
        trackEvent(AMPLITUDE_EVENTS.latestSignupStepAccessed, {
          'Latest page': 'Not set',
        });

        handleMinimumDataRequirementsRedirect();
        return;
      }

      handleLatestStepRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, state, stateFetching, stateStatus, isLoadingApplication]);

  return <>{children}</>;
};
